import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Drumstick, Egg } from "lucide-react";
import { CATEGORIES, DISHES } from "../../data/menu";

function TypeTag({ type }) {
  const map = {
    veg: { icon: Leaf, color: "#1e5128", label: "Veg" },
    nonveg: { icon: Drumstick, color: "#7a1f1f", label: "Non-Veg" },
    egg: { icon: Egg, color: "#d97706", label: "Egg" },
  };
  const { icon: Icon, color, label } = map[type] || map.veg;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-[#f7f0e0] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ring-1"
      style={{ color, boxShadow: `inset 0 0 0 1px ${color}33` }}
    >
      <Icon size={12} style={{ color }} /> {label}
    </span>
  );
}

export default function MenuGallery() {
  const [active, setActive] = useState("all");
  const dishes = active === "all" ? DISHES : DISHES.filter((d) => d.cat === active);

  return (
    <section id="menu" className="relative bg-[#eae0c9] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-sm uppercase tracking-[0.3em] font-bold text-[#d97706] mb-3"
            >
              Our Kitchen Serves
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-5xl md:text-6xl font-bold tracking-tight text-[#331818]"
            >
              The Menu
            </motion.h2>
          </div>
          <p className="max-w-sm text-[#5c3a3a]">
            A living taste of Maharashtra — pick a category and explore the
            dishes we lovingly prepare each day.
          </p>
        </div>

        {/* Filters */}
        <div data-testid="menu-filters" className="flex flex-wrap gap-2.5 mb-12">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                active === c.id
                  ? "bg-[#7a1f1f] text-[#f7f0e0]"
                  : "bg-[#f7f0e0] text-[#5c3a3a] ring-1 ring-[#7a1f1f]/15 hover:ring-[#7a1f1f]/40"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
          <AnimatePresence mode="popLayout">
            {dishes.map((d, i) => (
              <motion.article
                key={d.name}
                layout
                data-testid={`dish-card-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, delay: (i % 8) * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className="group overflow-hidden rounded-2xl bg-[#f7f0e0] ring-1 ring-[#7a1f1f]/12 shadow-[0_8px_30px_rgba(122,31,31,0.06)]"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={d.img}
                    alt={d.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.07]"
                  />
                  <div className="absolute top-3 left-3">
                    <TypeTag type={d.type} />
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-display text-xl md:text-2xl font-semibold leading-tight text-[#331818]">
                    {d.name}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5c3a3a]">{d.speciality}</p>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <p className="mt-10 text-center text-sm italic text-[#5c3a3a]">
          Menu changes with the season &amp; the day's fresh produce.
        </p>
      </div>
    </section>
  );
}
